// App shell + hash router. Composition root: owns session + task state +
// CRUD callbacks. As of Lane B.5.b, all state mutations go through the
// Hono Worker API instead of localStorage.

import { useState, useEffect, useCallback, useRef } from 'react';
import * as api from './lib/api.ts';
import { connectTaskSync, reconcileTasks, createSyncScheduler } from './lib/taskSync.js';
import { parseHash, navigate } from './lib/routing.js';
import { Sidebar } from './components/Sidebar.jsx';
import { PushPrompt } from './components/PushPrompt.jsx';
import { Toast } from './components/atoms.jsx';
import { ConfirmHost } from './components/ConfirmDialog.jsx';
import { NewTaskModal } from './components/NewTaskModal.jsx';
import { Login } from './routes/Login.jsx';
import { MyTasks } from './routes/MyTasks.jsx';
import { TaskDetail } from './routes/TaskDetail.jsx';
import { AdminOverview } from './routes/AdminOverview.jsx';
import { AdminPerson } from './routes/AdminPerson.jsx';
import { AdminUsers } from './routes/AdminUsers.jsx';
import { AdminHR } from './routes/AdminHR.jsx';
import { AdminDataReset } from './routes/AdminDataReset.jsx';
import { AdminCustomTakers } from './routes/AdminCustomTakers.jsx';
import { TrashView } from './routes/TrashView.jsx';
import { Profile } from './routes/Profile.jsx';
import { SetPassword } from './routes/SetPassword.jsx';
import { Inventory } from './routes/Inventory.jsx';
import { InventoryProducts } from './routes/InventoryProducts.jsx';
import { InventoryProductDetail } from './routes/InventoryProductDetail.jsx';
import { InventoryThresholds } from './routes/InventoryThresholds.jsx';
import { InventoryWaste } from './routes/InventoryWaste.jsx';
import { InventoryStats } from './routes/InventoryStats.jsx';
import { InventoryActivity } from './routes/InventoryActivity.jsx';
import { PrepEntry } from './routes/PrepEntry.jsx';
import { KitchenSheet } from './routes/KitchenSheet.jsx';
import { AdminOps } from './routes/AdminOps.jsx';
import { AdminGroups } from './routes/AdminGroups.jsx';
import { AdminTemplates } from './routes/AdminTemplates.jsx';
import { AdminReports } from './routes/AdminReports.jsx';
import { GroupDetail } from './routes/GroupDetail.jsx';
import { useTelemetry } from './lib/telemetry.ts';
import { canManageUsers, isElevated, canAccessHR, isAdmin, isCsManager } from './lib/auth.ts';
import { currentBuildId, fetchDeployedBuildId, isUpdateAvailable } from './lib/version.ts';

const App = () => {
  const [user, setUser] = useState(() => api.loadSession()?.staff ?? null);
  const [tasks, setTasks] = useState(null);            // null = not yet loaded
  const [staff, setStaff] = useState([]);              // global roster (for pickers)
  const [groups, setGroups] = useState([]);            // taxonomy (loaded once after login)
  const [route, setRoute] = useState(() => parseHash().path);
  const [showCreate, setShowCreate] = useState(false);
  const [prefillParent, setPrefillParent] = useState('');
  const [prefillGroup, setPrefillGroup] = useState('');
  const [prefillAssignee, setPrefillAssignee] = useState('');

  // Unified opener — pages call openCreate({ groupId?, assigneeId?, parentId? })
  // and the modal opens with those pre-set.
  function openCreate(opts = {}) {
    setPrefillParent(opts.parentId || '');
    setPrefillGroup(opts.groupId || '');
    setPrefillAssignee(opts.assigneeId || '');
    setShowCreate(true);
  }
  function closeCreate() {
    setShowCreate(false);
    setPrefillParent('');
    setPrefillGroup('');
    setPrefillAssignee('');
  }
  const [toast, setToast] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [wsStatus, setWsStatus] = useState('connecting');   // 'connecting'|'online'|'offline'
  const [lastSync, setLastSync] = useState(null);            // ms of last successful task refetch
  // Real-time sync scheduler — lets mutation handlers register their in-flight
  // window so a remote refresh can't clobber an optimistic edit (see taskSync).
  const syncRef = useRef(null);

  // On load, re-hydrate the current staff from the server so capability changes
  // (e.g. super_admin, role updates) reach already-logged-in users without a
  // forced re-login. Keeps the cached user on a transient error.
  useEffect(() => {
    if (!api.loadSession()) return;
    api.fetchMe().then(setUser).catch(() => {});
  }, []);

  // Per-route document.title so bookmarks + tab-switch identify the page.
  // Falls back to the generic title for unmapped routes.
  useEffect(() => {
    const ROUTE_TITLES = {
      '/inventory':            '当日现货 · VAVA',
      '/inventory/stats':      '统计报表 · VAVA',
      '/orders/entry':         '备货录入 · VAVA',
      '/orders/kitchen':       '后厨汇总 · VAVA',
      '/inventory/products':   '管理产品 · VAVA',
      '/inventory/thresholds': '低库存阈值 · VAVA',
      '/inventory/waste':      '报废分析 · VAVA',
      '/inventory/activity':   '活动日志 · VAVA',
      '/my-tasks':             '我的待办 · VAVA',
      '/admin':                '待办总览 · VAVA',
      '/admin/users':          '账号管理 · VAVA',
      '/admin/hr':             '人事档案 · VAVA',
      '/admin/data-reset':     '数据清零 · VAVA',
      '/admin/groups':         '分类管理 · VAVA',
      '/admin/templates':      '项目模板 · VAVA',
      '/admin/reports':        '月度报告 · VAVA',
      '/admin/ops':            '运营 · VAVA',
      '/profile':              '个人资料 · VAVA'
    };
    const t = ROUTE_TITLES[route];
    if (t) document.title = t;
    else if (route.startsWith('/inventory/products/')) document.title = '产品详情 · VAVA';
    else if (route.startsWith('/task/'))                document.title = '待办详情 · VAVA';
    else if (route.startsWith('/group/'))               document.title = '分组详情 · VAVA';
    else document.title = 'VAVA Designer Cakes';
  }, [route]);

  // Mount telemetry once user is authed (idempotent via strict-mode guard).
  useTelemetry(!!user);

  // Register the service worker once on first mount (so push subscriptions
  // can happen). Token mirror to IndexedDB lives inside api.login / logout.
  useEffect(() => {
    import('./lib/push.ts').then(m => m.registerServiceWorker?.()).catch(() => {});
    // Re-mirror token on app load if user is already authed (returning visit)
    if (user) {
      const tok = localStorage.getItem('vava_token');
      if (tok) import('./lib/push.ts').then(m => m.mirrorTokenToIdb(tok)).catch(() => {});
    }
  }, []);

  // Load tasks + staff after login. Re-runs whenever user changes (logout
  // → null → no fetch; login → fetch).
  useEffect(() => {
    if (!user) {
      setTasks(null);
      return;
    }
    let cancelled = false;
    setLoadErr('');
    Promise.all([api.loadTasks(), api.listStaff(), api.listGroups()])
      .then(([t, s, g]) => {
        if (cancelled) return;
        setTasks(t);
        setStaff(s);
        setGroups(g);
      })
      .catch((e) => {
        if (cancelled) return;
        // 401 = session expired (e.g. server restart wiped sessions)
        if (e.status === 401) {
          api.clearSession();
          setUser(null);
        } else {
          setLoadErr(e.message || '加载失败');
        }
      });
    return () => { cancelled = true; };
  }, [user]);

  // Real-time task sync. While logged in, hold a WebSocket to the TaskHub DO;
  // on a 'tasks-changed' signal, refetch /tasks and reconcile by id (preserving
  // identity for unchanged rows, deferring while the local user's own edit is in
  // flight). Replaces "stale until manual reload" for multi-user edits.
  useEffect(() => {
    if (!user) { syncRef.current = null; return; }
    const refetch = () => api.loadTasks()
      .then(fresh => { setTasks(cur => (cur ? reconcileTasks(cur, fresh) : fresh)); setLastSync(Date.now()); })
      .catch(() => {});
    const scheduler = createSyncScheduler(refetch);
    syncRef.current = scheduler;
    const handle = connectTaskSync(scheduler.onSignal, setWsStatus);
    return () => { handle.close(); syncRef.current = null; setWsStatus('connecting'); };
  }, [user]);

  // Support `?addChildOf=TXXX` query (from task detail "+ 加子项")
  useEffect(() => {
    const m = window.location.hash.match(/[?&]addChildOf=([^&]+)/);
    if (m && isElevated(user)) {
      setPrefillParent(m[1]);
      setShowCreate(true);
      const cleanHash = window.location.hash.replace(/[?&]addChildOf=[^&]+/, '');
      history.replaceState(null, '', cleanHash);
    }
  }, [route, user]);

  useEffect(() => {
    function onHash() { setRoute(parseHash().path); }
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Redirect root to the right landing page
  useEffect(() => {
    if (!user) return;
    if (route === '/' || route === '') {
      navigate(isElevated(user) ? '/admin' : '/my-tasks');
    }
  }, [user, route]);

  // New-version watcher: poll /version.json on load, on tab-focus, and every
  // 10 min. When the live build id differs from this running bundle's, show a
  // refresh banner so a long-open session doesn't keep writing with stale UI
  // after a deploy (Tiger, 2026-06-04).
  const [updateReady, setUpdateReady] = useState(false);
  useEffect(() => {
    let stopped = false;
    async function check() {
      if (updateReady) return;
      const deployed = await fetchDeployedBuildId();
      if (!stopped && isUpdateAvailable(currentBuildId(), deployed)) setUpdateReady(true);
    }
    check();
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    const iv = setInterval(check, 10 * 60 * 1000);
    return () => { stopped = true; document.removeEventListener('visibilitychange', onVis); clearInterval(iv); };
  }, [updateReady]);

  function onLogin(u) {
    setUser(u);
    navigate(isElevated(u) ? '/admin' : '/my-tasks');
  }

  async function logout() {
    await api.logout();
    setUser(null);
    setTasks(null);
    navigate('/');
  }

  // Optimistic update: patch local state, send PATCH, revert + toast on failure.
  const updateTask = useCallback(async (id, patch) => {
    const prevTask = tasks?.find(t => t.id === id);   // per-task snapshot for a scoped revert
    // Start the request + register its in-flight window BEFORE the optimistic
    // patch, so a concurrent remote 'tasks-changed' can't slip into the gap and
    // reconcile stale server state over the edit we're about to make.
    const p = api.updateTask(id, {
      title: patch.title,
      description: patch.detail ?? patch.description,
      status: patch.status,
      priority: patch.priority,
      due_date: patch.dueDate,
      admin_note: patch.adminNote,
      block_reason: patch.blockReason,
      group_id: patch.groupId,
      parent_id: patch.parentId
    });
    syncRef.current?.track(p);
    setTasks(ts => ts ? ts.map(t => t.id === id ? { ...t, ...patch } : t) : ts);   // optimistic
    try {
      const updated = await p;
      // Replace with server truth (in case of completed_at, updated_at, etc.)
      setTasks(ts => ts ? ts.map(t => t.id === id ? updated : t) : ts);
    } catch (e) {
      // Revert ONLY this task's row — never the whole array — so a failed PATCH
      // can't clobber other users' changes that a WS reconcile landed meanwhile.
      if (prevTask) setTasks(ts => ts ? ts.map(t => t.id === id ? prevTask : t) : ts);
      setToast({ kind: 'error', msg: e.message || '保存失败' });
      throw e;
    }
  }, [tasks]);

  const replaceOwners = useCallback(async (taskId, owners) => {
    try {
      const updated = await api.replaceOwners(taskId, owners);
      setTasks(ts => ts ? ts.map(t => t.id === updated.id ? updated : t) : ts);
      setToast({ kind: 'success', msg: '负责人已更新' });
      return updated;
    } catch (e) {
      setToast({ kind: 'error', msg: e.message || '更新失败' });
      throw e;
    }
  }, []);

  const deleteTask = useCallback(async (taskId) => {
    try {
      const p = api.deleteTask(taskId);
      syncRef.current?.track(p);
      await p;
      // Soft delete cascades to the whole subtree on the server — mirror that
      // locally so deep descendants don't linger as orphans until next reload.
      setTasks(ts => {
        if (!ts) return ts;
        const gone = new Set([taskId]);
        let changed = true;
        while (changed) {
          changed = false;
          for (const t of ts) {
            if (t.parentId && gone.has(t.parentId) && !gone.has(t.id)) { gone.add(t.id); changed = true; }
          }
        }
        return ts.filter(t => !gone.has(t.id));
      });
      // Inline 撤销: one-click restore from the toast, no trip to the 已删除 page.
      // Reload is inlined (not via reloadTasks, declared later — TDZ) to keep
      // this callback dependency-free.
      setToast({
        kind: 'success', msg: '已移入垃圾桶',
        action: {
          label: '撤销',
          onClick: async () => {
            try { await api.restoreTask(taskId); setTasks(await api.loadTasks()); setToast({ kind: 'success', msg: '已恢复' }); }
            catch (e) { setToast({ kind: 'error', msg: e.message || '恢复失败，请到「已删除」里恢复' }); }
          },
        },
      });
      navigate('/admin');
    } catch (e) {
      setToast({ kind: 'error', msg: e.message || '删除失败' });
      throw e;
    }
  }, []);

  // Reload all tasks from the server (used after restoring from the trash bin).
  const reloadTasks = useCallback(() => {
    api.loadTasks().then(t => setTasks(t)).catch(() => {});
  }, []);

  const refreshStaff = useCallback(async () => {
    try {
      const s = await api.listStaff();
      setStaff(s);
    } catch {
      // best-effort; AdminUsers already surfaces its own errors
    }
  }, []);

  const addUpdate = useCallback(async (taskId, text) => {
    try {
      await api.addTaskUpdate(taskId, text);
      // Refetch this task so the timeline shows the new entry
      const fresh = await api.getTask(taskId);
      setTasks(ts => ts ? ts.map(t => t.id === taskId ? fresh : t) : ts);
    } catch (e) {
      setToast({ kind: 'error', msg: e.message || '保存进度失败' });
      throw e;
    }
  }, []);

  async function createTask(form) {
    try {
      const owners = (form.owners && form.owners.length > 0)
        ? form.owners.map(o => ({ staffId: o.staffId, role: o.role, isPrimary: !!o.isPrimary }))
        : (form.assigneeId ? [{ staffId: form.assigneeId, role: 'R', isPrimary: true }] : []);
      const cp = api.createTask({
        title: form.title.trim(),
        description: form.detail || undefined,
        priority: form.priority,
        category: form.category,
        group_id: form.groupId || null,
        due_date: form.dueDate || null,
        parent_id: form.parentId || null,
        recurring: form.recurring,
        // Worker expects snake_case `staff_id` in the body; map here.
        owners: owners.map(o => ({ staff_id: o.staffId, role: o.role, is_primary: !!o.isPrimary }))
      });
      syncRef.current?.track(cp);
      const created = await cp;
      setTasks(ts => ts ? [created, ...ts] : [created]);
      setShowCreate(false);
      const responsibles = (created.owners ?? []).filter(o => o.role === 'R');
      const rNames = responsibles
        .map(o => staff.find(s => s.id === o.staffId)?.name)
        .filter(Boolean)
        .join('、');
      const otherCount = (created.owners ?? []).length - responsibles.length;
      const parent = created.parentId ? tasks?.find(t => t.id === created.parentId) : null;
      setToast({ kind: 'success', msg: parent
        ? `已创建子项 · 已通知 ${rNames}${otherCount > 0 ? ` + ${otherCount} 人` : ''}（项目：${parent.title.slice(0, 14)}...）`
        : `已创建 · 已通知 ${rNames}${otherCount > 0 ? ` + ${otherCount} 人` : ''}` });
    } catch (e) {
      setToast({ kind: 'error', msg: e.message || '创建待办失败' });
    }
  }

  // Set-password is a standalone page reachable while logged out — a new hire
  // opens the invite link before they have any session.
  if (route === '/set-password') {
    return <SetPassword onLogin={onLogin} />;
  }

  if (!user) return <Login onLogin={onLogin} />;

  // Loading state while initial fetch is in flight
  if (tasks === null) {
    return (
      <div className="vava-shell">
        <Sidebar user={user} onLogout={logout} current={route} wsStatus={wsStatus} lastSync={lastSync} />
        <main className="vava-main" role="main">
          <div className="vava-card empty-state">
            <div className="emoji">⏳</div>
            <div className="title">{loadErr ? '加载失败' : '加载中…'}</div>
            <div className="sub">
              {loadErr
                ? <>无法连接后端 · {loadErr}<br/>检查 worker 是否在 <span className="mono">http://localhost:8787</span> 运行</>
                : '正在从服务器拉取待办'}
            </div>
            {loadErr && (
              <button className="btn btn-primary" onClick={() => setUser({ ...user })}>重试</button>
            )}
          </div>
        </main>
        {wsStatus === 'offline' && (
          <div className="vava-offline-bar" role="status">
            <span>实时同步已断开 · 界面数据可能不是最新，重连后自动刷新</span>
          </div>
        )}
      </div>
    );
  }

  let page;
  if (route.startsWith('/task/')) {
    page = <TaskDetail user={user} tasks={tasks} updateTask={updateTask} addUpdate={addUpdate} createTask={createTask} replaceOwners={replaceOwners} deleteTask={deleteTask} staff={staff} groups={groups} />;
  } else if (route.startsWith('/group/')) {
    page = <GroupDetail user={user} tasks={tasks} staff={staff} groups={groups}
                        updateTask={updateTask} addUpdate={addUpdate}
                        onCreate={openCreate} />;
  } else if (route === '/admin' || route === '/admin/') {
    page = <AdminOverview user={user} tasks={tasks} staff={staff} groups={groups}
                          onCreate={openCreate} />;
  } else if (route === '/admin/ops') {
    page = <AdminOps user={user} />;
  } else if (route === '/admin/groups') {
    page = <AdminGroups user={user} />;
  } else if (route === '/admin/templates') {
    page = <AdminTemplates user={user} staff={staff} groups={groups} />;
  } else if (route === '/admin/reports') {
    page = <AdminReports user={user} staff={staff} />;
  } else if (route === '/admin/users') {
    // 账号管理 — every 管理员 (admin role), the 超级管理员 (Regina, full powers), and
    // a 客服管理员 (scoped). 店长 stays out. Per-route guards + super-admin-account
    // protection enforce the finer rules server-side.
    if (!(isAdmin(user) || user?.superAdmin || isCsManager(user))) { navigate('/my-tasks'); page = null; }
    else page = <AdminUsers user={user} onStaffChanged={refreshStaff} />;
  } else if (route === '/admin/hr') {
    // 人事档案 — admin / 店长 / 领班 (super-admin sees restricted manager profiles).
    if (!canAccessHR(user)) { navigate('/my-tasks'); page = null; }
    else page = <AdminHR user={user} />;
  } else if (route === '/admin/data-reset') {
    // 数据清零 — testing-stage data reset, 店长以上.
    if (!isElevated(user)) { navigate('/my-tasks'); page = null; }
    else page = <AdminDataReset user={user} />;
  } else if (route === '/admin/custom-takers') {
    // 自定义取走人 — manage non-staff 店员取走 names, 店长以上.
    if (!isElevated(user)) { navigate('/my-tasks'); page = null; }
    else page = <AdminCustomTakers user={user} />;
  } else if (route === '/admin/trash') {
    // 垃圾桶 — soft-deleted tasks: view / restore / permanently delete, 店长以上.
    if (!isElevated(user)) { navigate('/my-tasks'); page = null; }
    else page = <TrashView user={user} staff={staff} onChanged={reloadTasks} />;
  } else if (route === '/profile') {
    page = <Profile user={user} onUpdated={setUser} />;
  } else if (route === '/inventory') {
    page = <Inventory user={user} />;
  } else if (route === '/inventory/products') {
    page = <InventoryProducts user={user} />;
  } else if (route === '/inventory/thresholds') {
    page = <InventoryThresholds user={user} />;
  } else if (route === '/inventory/waste') {
    page = <InventoryWaste user={user} />;
  } else if (route === '/inventory/stats') {
    page = <InventoryStats user={user} />;
  } else if (route === '/inventory/activity') {
    page = <InventoryActivity user={user} />;
  } else if (route === '/orders/entry') {
    page = <PrepEntry user={user} />;
  } else if (route === '/orders/kitchen') {
    page = <KitchenSheet user={user} />;
  } else if (route.startsWith('/inventory/products/')) {
    const id = route.slice('/inventory/products/'.length);
    page = <InventoryProductDetail productId={id} />;
  } else if (route.startsWith('/people/') || route.startsWith('/admin/staff/') || route.startsWith('/admin/people/')) {
    // Per-person performance view. Open to ALL authenticated users — anyone
    // can see how anyone else is performing (12-person company, small team,
    // shared accountability). Old admin-scoped URLs stay routable as aliases.
    page = <AdminPerson user={user} tasks={tasks} staff={staff} onCreate={openCreate} />;
  } else {
    page = <MyTasks user={user} tasks={tasks} staff={staff}
                    onCreate={openCreate} />;
  }

  return (
    <div className="vava-shell">
      <Sidebar user={user} onLogout={logout} current={route} wsStatus={wsStatus} lastSync={lastSync} />
      <main className="vava-main" role="main">
        {page}
      </main>
      {showCreate && (
        <NewTaskModal onClose={closeCreate}
                      onCreate={createTask}
                      staff={staff}
                      tasks={tasks}
                      groups={groups}
                      prefillParent={prefillParent}
                      prefillGroup={prefillGroup}
                      prefillAssignee={prefillAssignee} />
      )}
      {toast && <Toast message={toast.msg} kind={toast.kind} action={toast.action} onDone={() => setToast(null)} />}
      <ConfirmHost />
      {user && wsStatus === 'offline' && (
        <div className="vava-offline-bar" role="status">
          <span>实时同步已断开 · 界面数据可能不是最新，重连后自动刷新</span>
        </div>
      )}
      <PushPrompt user={user} />
      {updateReady && (
        <div className="vava-update-banner" role="status">
          <span>已发布新版本</span>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>刷新</button>
        </div>
      )}
    </div>
  );
};

export default App;
